import { ApiUploadError, createIdempotencyKey } from "@lafayette/api-client";
import type {
  RequestQuestionField,
  StaffRequestAttachmentBody,
  StaffRequestAttachmentResult,
  StaffRequestCommandResult,
  StaffRequestCustomerUpdateResult,
  StaffRequestEvidenceInput,
  StaffRequestEvidenceResult,
  StaffRequestNoteResult,
} from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { describeRequestError, type RequestFailure } from "./errors";
import { requestKeys } from "./queries";
import { useRequestsRuntime } from "./runtime";

// Runs a staff request command (MD-R3 to MD-R11) and keeps the pages current. Three rules live here:
//   1. Idempotency. One dialog (its `scope`) has one `Idempotency-Key` for each body it sends; `release(scope)` drops them when the dialog closes. After a failure where the command may have run (no answer, a 5xx, a 429)
//      the next identical attempt reuses the key, so the server runs it once and replays the stored answer. A changed input, or an
//      action after a success or a refusal, gets a new key (the same key with another body would be refused as a key reused).
//   2. Version. Every command that changes the request sends the `expectedVersion` the page read. The page never guesses a version: a
//      stale one is a 409, and the user decides again on the reloaded request.
//   3. Reload. After a success the answer's `detail` becomes the page's detail at once. After a refusal that says the request moved
//      (409), every request read is marked stale and the open pages read again. A refused command changed nothing.
//
// Nothing here logs. A body holds a staff reason or a note, and neither goes anywhere but the request.

type VersionReason = Readonly<{ expectedVersion: number; reason: string }>;
type Closure = VersionReason & Readonly<{ customerStatusSummary?: string | null }>;

export type RequestCommand =
  | Readonly<{ command: "claim"; requestId: string; body: VersionReason }>
  | Readonly<{
      command: "reassign";
      requestId: string;
      body: VersionReason & Readonly<{ assigneeStaffUserId: string }>;
    }>
  | Readonly<{
      command: "askCustomer";
      requestId: string;
      body: VersionReason &
        Readonly<{ question: string; fields?: readonly RequestQuestionField[] }>;
    }>
  | Readonly<{
      command: "workStatus";
      requestId: string;
      body: VersionReason & Readonly<{ workStatus: "NONE" | "WITH_SUPPLIER" | "INTERNAL_REVIEW" }>;
    }>
  | Readonly<{ command: "unavailable" | "decline" | "cancel"; requestId: string; body: Closure }>
  | Readonly<{
      command: "complete";
      requestId: string;
      body: VersionReason &
        Readonly<{
          evidenceKind:
            | "SERVICE_DELIVERED"
            | "CUSTOMER_ACKNOWLEDGED"
            | "SUPPLIER_REPORTED_FULFILMENT";
        }>;
    }>
  | Readonly<{
      command: "priority";
      requestId: string;
      body: VersionReason & Readonly<{ priority: "STANDARD" | "HIGH" | "URGENT" }>;
    }>
  | Readonly<{
      command: "statusSummary";
      requestId: string;
      body: VersionReason & Readonly<{ summary: string }>;
    }>
  | Readonly<{
      command: "note";
      requestId: string;
      body: Readonly<{
        kind: "SUPPLIER_CONTACT" | "SUPPLIER_QUOTE_CONTEXT" | "STAFF_NOTE" | "HANDOVER";
        body: string;
      }>;
    }>;

export type FulfilmentCommand =
  | Readonly<{
      command: "confirm";
      requestId: string;
      body: VersionReason & Readonly<{ evidenceId: string }>;
    }>
  | Readonly<{ command: "recordEvidence"; requestId: string; body: StaffRequestEvidenceInput }>
  | Readonly<{
      command: "publishUpdate";
      requestId: string;
      body: Readonly<{
        kind:
          | "NEXT_STEP"
          | "CONFIRMATION_DETAILS"
          | "ALTERNATIVES"
          | "UNAVAILABLE_NOTE"
          | "RESTRICTIONS";
        text: string;
        evidenceId?: string | null;
      }>;
    }>
  | Readonly<{
      /**
       * CHT-754: uploads one file, then attaches it. Three calls: a signed target, the browser's PUT to storage, and the completion that
       * the server checks against the stored bytes. `body` is what the browser says about the file (and what the idempotency signature
       * reads); `file` is the file itself.
       */
      command: "uploadAttachment";
      requestId: string;
      body: Readonly<{
        displayName: string;
        contentType: StaffRequestAttachmentBody["contentType"];
        sizeBytes: number;
      }>;
      file: Blob;
    }>
  | Readonly<{
      command: "shareAttachment";
      requestId: string;
      attachmentId: string;
      body: Readonly<{ reason: string }>;
    }>
  | Readonly<{
      command: "unavailableWithAlternatives";
      requestId: string;
      body: VersionReason &
        Readonly<{
          detail: string;
          supplierLabel?: string | null;
          contactMethod?: "PHONE" | "EMAIL" | "WHATSAPP" | "IN_PERSON" | "OTHER" | null;
          customerNote?: string | null;
          alternatives?: string | null;
          customerStatusSummary?: string | null;
        }>;
    }>
  | Readonly<{
      command: "correctConfirmation";
      requestId: string;
      body: VersionReason &
        Readonly<{
          detail: string;
          customerText: string;
          customerUpdateKind?: "CONFIRMATION_DETAILS" | "NEXT_STEP" | "RESTRICTIONS" | null;
        }>;
    }>;

export type RequestCommandOutcome =
  | StaffRequestCommandResult
  | StaffRequestNoteResult
  | StaffRequestEvidenceResult
  | StaffRequestCustomerUpdateResult
  | StaffRequestAttachmentResult;

export type RequestCommandResult =
  | Readonly<{ ok: true; outcome: RequestCommandOutcome }>
  | Readonly<{ ok: false; failure: RequestFailure }>;

export type AnyRequestCommand = RequestCommand | FulfilmentCommand;

const signatureOf = (request: AnyRequestCommand, scope: string): string =>
  JSON.stringify([
    scope,
    request.command,
    request.requestId,
    "attachmentId" in request ? request.attachmentId : null,
    request.body,
  ]);

export function useRequestCommands() {
  const { api, fulfilment, uploadFile } = useRequestsRuntime();
  const queryClient = useQueryClient();
  const keys = useRef(new Map<string, string>());
  // An upload that was sent but not completed: a retry of the same file does not send the bytes again once they are there. Then a retry
  // after a lost answer attaches the SAME upload (the server answers the same attachment), never a second file.
  const uploads = useRef(new Map<string, { uploadId: string; sent: boolean }>());
  // A failure that may have run (no answer, 429, 5xx) leaves the page unsure whether the change happened. The page reads again when
  // the staff member closes the dialog, so it never keeps showing the state from before.
  const mayHaveRun = useRef(false);
  const [pendingCount, setPendingCount] = useState(0);

  const sendUpload = useCallback(
    async (
      request: Extract<FulfilmentCommand, { command: "uploadAttachment" }>,
      idempotencyKey: string,
      signature: string,
    ): Promise<RequestCommandOutcome> => {
      let upload = uploads.current.get(signature);
      if (upload === undefined || !upload.sent) {
        // The bytes are not in storage yet (a first try, or the PUT failed): ask for a target (an old one may have expired) and send them.
        const target = await fulfilment.createAttachmentUploadTarget(
          request.requestId,
          { contentType: request.body.contentType, sizeBytes: request.body.sizeBytes },
          { idempotencyKey },
        );
        upload = { uploadId: target.uploadId, sent: false };
        uploads.current.set(signature, upload);
        try {
          await uploadFile(target, request.file);
        } catch {
          // No cause is kept: the message of a failed PUT can hold the signed URL.
          throw new ApiUploadError(0, "the file could not be sent to storage");
        }
        upload.sent = true;
      }
      return fulfilment.addAttachment(
        request.requestId,
        {
          uploadId: upload.uploadId,
          displayName: request.body.displayName,
          contentType: request.body.contentType,
        },
        { idempotencyKey },
      );
    },
    [fulfilment, uploadFile],
  );

  const send = useCallback(
    (
      request: AnyRequestCommand,
      idempotencyKey: string,
      signature: string,
    ): Promise<RequestCommandOutcome> => {
      const options = { idempotencyKey };
      const id = request.requestId;
      switch (request.command) {
        case "claim":
          return api.claim(id, request.body, options);
        case "reassign":
          return api.reassign(id, request.body, options);
        case "askCustomer":
          return api.askCustomer(id, request.body, options);
        case "workStatus":
          return api.setWorkStatus(id, request.body, options);
        case "unavailable":
          return api.markUnavailable(id, request.body, options);
        case "decline":
          return api.decline(id, request.body, options);
        case "cancel":
          return api.cancel(id, request.body, options);
        case "complete":
          return api.complete(id, request.body, options);
        case "priority":
          return api.setPriority(id, request.body, options);
        case "statusSummary":
          return api.updateStatusSummary(id, request.body, options);
        case "note":
          return api.addNote(id, request.body, options);
        case "confirm":
          return api.confirm(id, request.body, options);
        case "recordEvidence":
          return fulfilment.record(id, request.body, options);
        case "publishUpdate":
          return fulfilment.publishUpdate(id, request.body, options);
        case "uploadAttachment":
          return sendUpload(request, idempotencyKey, signature);
        case "shareAttachment":
          return fulfilment.shareAttachment(id, request.attachmentId, request.body, options);
        case "unavailableWithAlternatives":
          return fulfilment.markUnavailable(id, request.body, options);
        case "correctConfirmation":
          return fulfilment.correctConfirmation(id, request.body, options);
      }
    },
    [api, fulfilment, sendUpload],
  );

  const run = useCallback(
    async (request: AnyRequestCommand, scope = ""): Promise<RequestCommandResult> => {
      const signature = signatureOf(request, scope);
      const key = keys.current.get(signature) ?? createIdempotencyKey();
      keys.current.set(signature, key);

      setPendingCount((count) => count + 1);
      try {
        const outcome = await send(request, key, signature);
        keys.current.delete(signature);
        uploads.current.delete(signature);
        mayHaveRun.current = false;
        if ("detail" in outcome && outcome.detail !== null) {
          queryClient.setQueryData(requestKeys.detail(request.requestId), outcome.detail);
        }
        // The queue, the questions and (for a note or a null detail) the detail itself are read again.
        void queryClient.invalidateQueries({ queryKey: requestKeys.all });
        return { ok: true, outcome };
      } catch (error) {
        const failure = describeRequestError(error);
        if (!failure.retryable) {
          keys.current.delete(signature);
          uploads.current.delete(signature);
        } else mayHaveRun.current = true;
        if (failure.reload) void queryClient.invalidateQueries({ queryKey: requestKeys.all });
        return { ok: false, failure };
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [queryClient, send],
  );

  /**
   * Call when a dialog closes (its `scope` is the id of that dialog). After a failure that may have run, the request is read again.
   * The keys of the dialog are dropped, so a NEW dialog with the same text sends a NEW idempotency key: a key lives only for the
   * retries inside the dialog that made it.
   */
  const release = useCallback(
    (scope: string) => {
      const prefix = `["${scope}",`;
      for (const signature of [...keys.current.keys()]) {
        if (signature.startsWith(prefix)) {
          keys.current.delete(signature);
          uploads.current.delete(signature);
        }
      }
      if (!mayHaveRun.current) return;
      mayHaveRun.current = false;
      void queryClient.invalidateQueries({ queryKey: requestKeys.all });
    },
    [queryClient],
  );

  return { run, pending: pendingCount > 0, release };
}
